import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, ShoppingBag, XCircle } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useCart } from "@/context/CartContext";
import { api } from "@/lib/api";

export default function StoreSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id") || params.get("token");
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [order, setOrder] = useState(null);
  const { clear } = useCart();
  const attempts = useRef(0);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/store/orders/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setOrder(data);
          setState("paid");
          if (!cleared.current) { clear(); cleared.current = true; }
          return;
        }
        attempts.current += 1;
        if (attempts.current >= 6) { setState("pending"); return; }
        timer = setTimeout(poll, 1500);
      } catch {
        setState("error");
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div>
      <Navbar />
      <section className="min-h-[70vh] flex items-center justify-center py-32 px-5">
        <div className="max-w-lg w-full text-center">
          {state === "checking" && (
            <>
              <Loader2 className="w-14 h-14 text-[#D90429] animate-spin mx-auto" />
              <h1 className="font-display text-3xl uppercase mt-6">Confirming Your Order…</h1>
              <p className="text-slate-500 mt-2">One moment while we verify your payment.</p>
            </>
          )}
          {state === "paid" && (
            <>
              <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto" />
              <h1 className="font-display text-4xl uppercase mt-6">Order Confirmed!</h1>
              <p className="text-slate-600 mt-3">Thank you for your order. A confirmation email is on its way. We'll get your gear moving right away.</p>
              {order?.items?.length > 0 && (
                <div className="bg-white border border-slate-200 text-left mt-6 p-5">
                  {order.items.map((it, idx) => (
                    <div key={idx} className="flex justify-between text-sm py-1">
                      <span className="text-slate-600">{it.name}{[it.size, it.color].filter(Boolean).length ? ` (${[it.size, it.color].filter(Boolean).join(", ")})` : ""} × {it.quantity}</span>
                      <span className="font-semibold text-[#0A1128]">${(it.price * it.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between border-t border-slate-200 mt-2 pt-2 font-bold text-[#0A1128]">
                    <span>Total</span><span>${order.subtotal?.toFixed(2)}</span>
                  </div>
                </div>
              )}
              <Link to="/store" className="inline-flex items-center gap-2 bg-[#0A1128] text-white px-8 py-3.5 uppercase tracking-[0.15em] text-sm font-bold hover:bg-[#1a2547] transition-colors duration-200 mt-8"><ShoppingBag className="w-4 h-4" /> Continue Shopping</Link>
            </>
          )}
          {state === "pending" && (
            <>
              <Loader2 className="w-14 h-14 text-[#F4A261] mx-auto" />
              <h1 className="font-display text-3xl uppercase mt-6">Payment Processing</h1>
              <p className="text-slate-500 mt-2">Your payment is still processing. You'll receive a confirmation email shortly.</p>
              <Link to="/store" className="inline-block bg-[#0A1128] text-white px-8 py-3.5 uppercase tracking-[0.15em] text-sm font-bold mt-8">Back To Store</Link>
            </>
          )}
          {state === "error" && (
            <>
              <XCircle className="w-14 h-14 text-[#D90429] mx-auto" />
              <h1 className="font-display text-3xl uppercase mt-6">Something Went Wrong</h1>
              <p className="text-slate-500 mt-2">We couldn't confirm this order. If you were charged, please contact us.</p>
              <Link to="/store" className="inline-block bg-[#0A1128] text-white px-8 py-3.5 uppercase tracking-[0.15em] text-sm font-bold mt-8">Back To Store</Link>
            </>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
